import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Loader2, Plus, X, Trophy, Users, Eye, Bookmark, Heart, Sparkles, Target, AlertTriangle } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";

const FORMATOS = ["Reels", "Carrossel", "Stories", "Estático"];
const EDITORIAS = ["Educativo", "Crença", "Inspiracional", "Histórias", "Análises"];

const OBJETIVO_LABELS = {
  awareness: "Awareness — Alcance & Reconhecimento",
  autoridade: "Autoridade — Educar & ganhar credibilidade",
  leads_mql: "Leads & MQL — Gerar demanda qualificada",
  comunidade: "Comunidade — Engajamento & retenção",
  vendas: "Vendas — Conversão direta",
};

function DiagnosticCard({ diagnostic, objetivoLabel }) {
  if (!diagnostic) return null;
  const d = diagnostic.diagnostic || {};
  if (d.error || !d.gargalo) {
    return (
      <div className="card p-5" data-testid="perf-diag-raw">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={16} className="text-[var(--warning)]" />
          <span className="text-sm font-semibold">Resposta bruta da IA</span>
        </div>
        <div className="text-xs text-muted mb-2">{d.error}</div>
        <pre className="text-xs whitespace-pre-wrap card-2 p-3 max-h-96 overflow-auto">{d.raw || JSON.stringify(d, null, 2)}</pre>
      </div>
    );
  }
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="perf-diagnostic-structured">
      <div className="card p-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{ background: "linear-gradient(120deg, #f87171 0%, #FF4E9E 100%)" }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-[var(--warning)]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted">// gargalo</span>
          </div>
          <div className="text-lg font-extrabold mb-1" data-testid="perf-diag-gargalo-etapa">{d.gargalo.etapa}</div>
          <p className="text-sm text-muted leading-relaxed" data-testid="perf-diag-gargalo-porque">{d.gargalo.por_que}</p>
        </div>
      </div>
      <div className="card p-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{ background: "var(--grad-soft)" }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Target size={14} className="grad-text" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted">// leitura vs objetivo</span>
          </div>
          <div className="text-xs grad-text font-bold mb-2 uppercase tracking-wide">{objetivoLabel}</div>
          <p className="text-sm leading-relaxed" data-testid="perf-diag-leitura">{d.leitura_vs_objetivo}</p>
        </div>
      </div>
      <div className="card p-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{ background: "linear-gradient(120deg, #4ade80 0%, #C05BFF 100%)" }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-[var(--success)]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted">// 3 recomendações</span>
          </div>
          <ol className="space-y-3" data-testid="perf-diag-recomendacoes">
            {(d.recomendacoes || []).slice(0, 3).map((r, i) => (
              <li key={i} className="text-sm" data-testid={`perf-diag-rec-${i}`}>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full grad-bg text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <div className="flex-1">
                    <div className="font-bold">{r.titulo}</div>
                    <div className="text-xs text-muted mt-0.5">{r.acao}</div>
                    {r.impacto_esperado && <div className="text-[11px] mt-1 chip inline-block" style={{ padding: "2px 6px" }}>↑ {r.impacto_esperado}</div>}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

function engagementRate(p) {
  const reach = Number(p.reach || 0);
  if (!reach) return 0;
  const eng = Number(p.likes || 0) + Number(p.comments || 0) + Number(p.shares || 0) + Number(p.saves || 0);
  return (eng / reach) * 100;
}

function KpiCard({ label, value, icon: Icon, suffix, testid }) {
  return (
    <div className="card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted">
        <Icon size={14} />
        <span className="text-[10px] uppercase tracking-widest font-mono">{label}</span>
      </div>
      <div className="text-2xl font-extrabold mt-2">
        {value}
        {suffix && <span className="text-sm text-muted font-normal ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function TrendChart({ series }) {
  const w = 600, h = 160, pad = 20;
  const max = Math.max(...series, 1);
  const step = (w - pad * 2) / Math.max(series.length - 1, 1);
  const pts = series.map((v, i) => [pad + i * step, h - pad - (v / max) * (h - pad * 2)]);
  const line = pts.map((p, i) => (i === 0 ? "M" : "L") + p[0] + "," + p[1]).join(" ");
  const area = line + ` L ${pts[pts.length - 1][0]},${h - pad} L ${pts[0][0]},${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid="trend-chart">
      <defs>
        <linearGradient id="tg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#C05BFF" stopOpacity="0.4" />
          <stop offset="1" stopColor="#C05BFF" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="tl" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#FF7A5A" />
          <stop offset="0.55" stopColor="#FF4E9E" />
          <stop offset="1" stopColor="#C05BFF" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#tg)" />
      <path d={line} fill="none" stroke="url(#tl)" strokeWidth="2.5" />
      {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={3} fill="#C05BFF" />)}
    </svg>
  );
}

function Breakdown({ label, groups, testid }) {
  const max = Math.max(...Object.values(groups).map((g) => g.avg), 1);
  return (
    <div className="card p-5" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted mb-3">// breakdown · {label}</div>
      <div className="space-y-2">
        {Object.entries(groups).sort((a, b) => b[1].avg - a[1].avg).map(([k, g]) => (
          <div key={k}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span>{k}</span>
              <span className="text-muted text-xs">{g.count} post{g.count === 1 ? "" : "s"} · {g.avg.toFixed(1)}%</span>
            </div>
            <div className="h-2 rounded" style={{ background: "var(--panel-2)" }}>
              <div className="h-full rounded" style={{ width: `${(g.avg / max) * 100}%`, background: "var(--grad)" }} />
            </div>
          </div>
        ))}
        {Object.keys(groups).length === 0 && <div className="text-xs text-muted">Sem dados ainda.</div>}
      </div>
    </div>
  );
}

function InlineNum({ value, onChange, testid }) {
  return <input type="number" className="input py-1.5 text-sm w-20" value={value ?? 0} onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} data-testid={testid} />;
}

function InlineText({ value, onChange, placeholder, testid }) {
  return <input className="input py-1.5 text-sm" value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
}

function InlineSelect({ value, onChange, options, testid }) {
  return (
    <select className="input py-1.5 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function Performance() {
  const { activeBrand } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const timers = useRef({});
  const pendingPost = useRef({}); // { [postId]: {mergedPatch} }
  const pendingTop = useRef({});

  const load = async () => {
    if (!activeBrand) return;
    try {
      const r = await api.get(`/performance/${activeBrand.brand_id}`);
      setData(r.data);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeBrand]);
  useEffect(() => () => {
    Object.values(timers.current).forEach((t) => clearTimeout(t));
    timers.current = {};
  }, []);

  const persistPost = (id, field, value) => {
    pendingPost.current[id] = { ...(pendingPost.current[id] || {}), [field]: value };
    if (timers.current[id]) clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(async () => {
      const patch = pendingPost.current[id];
      pendingPost.current[id] = {};
      setSaving(true);
      try { await api.put(`/performance/${activeBrand.brand_id}/post/${id}`, patch); }
      catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
      finally { setSaving(false); }
    }, 600);
  };

  const updatePost = (id, field, value) => {
    setData((s) => ({ ...s, posts: s.posts.map((p) => p.id === id ? { ...p, [field]: value } : p) }));
    persistPost(id, field, value);
  };

  const persistTop = (field, value) => {
    pendingTop.current[field] = value;
    if (timers.current["_top"]) clearTimeout(timers.current["_top"]);
    timers.current["_top"] = setTimeout(async () => {
      const patch = pendingTop.current;
      pendingTop.current = {};
      setSaving(true);
      try { await api.put(`/performance/${activeBrand.brand_id}`, patch); }
      catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
      finally { setSaving(false); }
    }, 600);
  };

  const updateFollowers = (v) => {
    setData((s) => ({ ...s, followers: v }));
    persistTop("followers", v);
  };

  const updateTrend = (idx, v) => {
    const trend = [...(data.trend || [])];
    trend[idx] = v;
    setData((s) => ({ ...s, trend }));
    persistTop("trend", trend);
  };

  const addPost = async () => {
    try {
      const r = await api.post(`/performance/${activeBrand.brand_id}/post`, { tema: "Novo post", fmt: "Reels", ed: "Educativo" });
      setData((s) => ({ ...s, posts: [...s.posts, r.data] }));
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const delPost = async (id) => {
    if (!window.confirm("Excluir este post?")) return;
    try {
      await api.delete(`/performance/${activeBrand.brand_id}/post/${id}`);
      setData((s) => ({ ...s, posts: s.posts.filter((p) => p.id !== id) }));
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const analyze = async () => {
    if (!activeBrand?.objetivo_social) {
      toast.error("Defina o objetivo da marca em Funil & Métricas primeiro");
      return;
    }
    setAnalyzing(true);
    try {
      const r = await api.post(`/performance/${activeBrand.brand_id}/analyze`);
      setData((s) => ({ ...s, diagnostic: r.data }));
      toast.success("Diagnóstico gerado");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const kpis = useMemo(() => {
    if (!data) return null;
    const posts = data.posts || [];
    const totalReach = posts.reduce((a, p) => a + Number(p.reach || 0), 0);
    const totalSaves = posts.reduce((a, p) => a + Number(p.saves || 0), 0);
    const engs = posts.map(engagementRate).filter((v) => isFinite(v));
    const avgEng = engs.length ? engs.reduce((a, v) => a + v, 0) / engs.length : 0;
    const bestIdx = engs.length ? engs.indexOf(Math.max(...engs)) : -1;
    const bestId = bestIdx >= 0 ? posts[bestIdx].id : null;
    return { totalReach, totalSaves, avgEng, bestId };
  }, [data]);

  const breakdown = (dim) => {
    if (!data) return {};
    const acc = {};
    (data.posts || []).forEach((p) => {
      const k = p[dim] || "—";
      const eng = engagementRate(p);
      if (!acc[k]) acc[k] = { total: 0, count: 0 };
      acc[k].total += eng;
      acc[k].count += 1;
    });
    return Object.fromEntries(Object.entries(acc).map(([k, v]) => [k, { count: v.count, avg: v.count ? v.total / v.count : 0 }]));
  };

  if (!data) return <div className="p-10 text-muted font-mono text-xs">carregando…</div>;

  return (
    <div className="p-6 lg:p-10" data-testid="performance-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// módulo · performance & interpretação</div>
          <h1 className="text-3xl font-extrabold">Performance de Posts</h1>
          <p className="text-muted text-sm mt-1 max-w-2xl">Você registra as métricas dos posts publicados. A IA lê o comportamento <strong>à luz do objetivo da sua marca em social</strong> e aponta o que está funcionando (ou não) para gerar autoridade, leads, comunidade ou vendas.</p>
        </div>
        <div className="flex items-center gap-3">
          {saving && <span className="text-xs text-muted flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> salvando…</span>}
          <div className="card-2 px-3 py-2 flex items-center gap-2">
            <Users size={14} className="text-muted" />
            <span className="text-[10px] uppercase tracking-widest text-muted font-mono">Seguidores</span>
            <input type="number" className="input py-1 text-sm w-24" value={data.followers ?? 0} onChange={(e) => updateFollowers(Number(e.target.value || 0))} data-testid="followers-input" />
          </div>
        </div>
      </div>

      {/* Analisar com IA */}
      <div className="card p-6 mb-6 relative overflow-hidden" data-testid="perf-analyze-hero">
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{ background: "var(--grad-soft)" }} />
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// leitura estratégica · IA</div>
            <h3 className="font-bold text-lg">Analisar performance com IA</h3>
            <p className="text-xs text-muted mt-1 max-w-lg">
              A IA cruza formatos, editorias e engajamento com o objetivo da marca e o posicionamento para apontar gargalo, leitura e 3 recomendações.
              {!activeBrand?.objetivo_social && <span className="block mt-1 text-[var(--warning)]">Defina o objetivo em Funil & Métricas primeiro.</span>}
            </p>
          </div>
          <button
            onClick={analyze}
            disabled={analyzing || !data.posts?.length || !activeBrand?.objetivo_social}
            className="btn-grad text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="perf-analyze-btn"
          >
            {analyzing ? <><Loader2 size={14} className="animate-spin" /> Analisando…</> : <><Sparkles size={14} /> Analisar com IA</>}
          </button>
        </div>
        {data.diagnostic && (
          <div className="relative mt-5">
            <div className="text-[10px] text-muted font-mono uppercase tracking-widest mb-2">// último diagnóstico · {new Date(data.diagnostic.generated_at).toLocaleString("pt-BR")}</div>
            <DiagnosticCard diagnostic={data.diagnostic} objetivoLabel={OBJETIVO_LABELS[activeBrand?.objetivo_social] || "Sem objetivo definido"} />
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Alcance total" value={kpis.totalReach.toLocaleString("pt-BR")} icon={Eye} testid="kpi-reach" />
        <KpiCard label="Engajamento médio" value={kpis.avgEng.toFixed(1)} suffix="%" icon={Heart} testid="kpi-eng" />
        <KpiCard label="Salvamentos" value={kpis.totalSaves.toLocaleString("pt-BR")} icon={Bookmark} testid="kpi-saves" />
        <KpiCard label="Novos seguidores" value={(data.followers || 0).toLocaleString("pt-BR")} icon={Users} testid="kpi-followers" />
      </div>

      {/* Trend chart */}
      <div className="card p-5 mb-6" data-testid="trend-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted">// tendência</div>
            <h3 className="font-bold">Alcance nas últimas 8 semanas</h3>
          </div>
        </div>
        <TrendChart series={data.trend || [0, 0, 0, 0, 0, 0, 0, 0]} />
        <div className="grid grid-cols-8 gap-2 mt-3">
          {(data.trend || [0, 0, 0, 0, 0, 0, 0, 0]).map((v, i) => (
            <div key={i} className="card-2 p-1.5">
              <div className="text-[9px] text-muted font-mono uppercase text-center">Sem {i + 1}</div>
              <input type="number" className="input py-1 text-xs w-full text-center" value={v} onChange={(e) => updateTrend(i, Number(e.target.value || 0))} data-testid={`trend-${i}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Breakdown label="por formato" groups={breakdown("fmt")} testid="breakdown-fmt" />
        <Breakdown label="por editoria" groups={breakdown("ed")} testid="breakdown-ed" />
      </div>

      {/* Posts table */}
      <div className="card overflow-hidden" data-testid="posts-table">
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted">// posts publicados</div>
            <div className="text-sm">Registre 4-8 posts recentes para a IA ter contexto</div>
          </div>
          <button onClick={addPost} className="btn-grad text-xs flex items-center gap-1" data-testid="new-perf-post"><Plus size={12} /> Novo post</button>
        </div>
        <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_80px_80px_80px_80px_80px_60px_40px] gap-0 px-4 py-2 border-b" style={{ background: "var(--panel-2)", borderColor: "var(--border)" }}>
          {["Tema", "Formato", "Editoria", "Alc.", "Cur.", "Com.", "Comp.", "Salv.", "Eng.%", ""].map((h, i) => <div key={i} className="font-mono text-[10px] uppercase tracking-widest text-muted">{h}</div>)}
        </div>
        {data.posts.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted">Nenhum post ainda. Adicione um pra começar a acompanhar métricas.</div>
        ) : data.posts.map((p) => {
          const eng = engagementRate(p);
          const isBest = kpis.bestId === p.id;
          return (
            <div key={p.id} className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1fr_80px_80px_80px_80px_80px_60px_40px] gap-2 items-center px-4 py-2 border-b" style={{ borderColor: "var(--border)" }} data-testid={`perf-row-${p.id}`}>
              <div className="flex items-center gap-2">
                {isBest && <Trophy size={12} className="text-[#FF4E9E] flex-shrink-0" title="Melhor engajamento" />}
                <InlineText value={p.tema} placeholder="título" onChange={(v) => updatePost(p.id, "tema", v)} testid={`perf-${p.id}-tema`} />
              </div>
              <InlineSelect value={p.fmt} options={FORMATOS} onChange={(v) => updatePost(p.id, "fmt", v)} testid={`perf-${p.id}-fmt`} />
              <InlineSelect value={p.ed} options={EDITORIAS} onChange={(v) => updatePost(p.id, "ed", v)} testid={`perf-${p.id}-ed`} />
              <InlineNum value={p.reach} onChange={(v) => updatePost(p.id, "reach", v)} testid={`perf-${p.id}-reach`} />
              <InlineNum value={p.likes} onChange={(v) => updatePost(p.id, "likes", v)} testid={`perf-${p.id}-likes`} />
              <InlineNum value={p.comments} onChange={(v) => updatePost(p.id, "comments", v)} testid={`perf-${p.id}-comments`} />
              <InlineNum value={p.shares} onChange={(v) => updatePost(p.id, "shares", v)} testid={`perf-${p.id}-shares`} />
              <InlineNum value={p.saves} onChange={(v) => updatePost(p.id, "saves", v)} testid={`perf-${p.id}-saves`} />
              <div className={`text-sm font-bold ${isBest ? "grad-text" : ""}`} data-testid={`perf-${p.id}-eng`}>{eng.toFixed(1)}%</div>
              <button onClick={() => delPost(p.id)} className="text-muted hover:text-[var(--error)]" data-testid={`perf-${p.id}-del`}><X size={14} /></button>
            </div>
          );
        })}
      </div>

      <div className="mt-4 card-2 p-3 text-xs text-muted">
        💡 <strong>Como usar:</strong> registre 4-8 posts recentes com métricas reais (Insights nativos ou planilha), rode a análise com IA e execute as 3 recomendações no próximo ciclo. É consultoria de conteúdo, não dashboard.
      </div>
    </div>
  );
}
